import { arrayEmpty } from 'src/app/utils/array.utils';
import { getXmlBoolean, getXmlLine, wrapByXmlTag } from 'src/app/utils/soap.utils';
import {
    CalculRegleAvanceeModelDto,
    CritereRegleAvanceeModelDto,
    MoleculeRegleAvanceeModelDto,
    RegleAvanceeModelDto,
    StatusDtoEnum
} from 'src/graphql/generated-types';
import { guid } from 'src/app/utils/guid.utils';

export function advancedMotherRuleToXML(motherRule: RegleAvanceeModelDto): string {
    return `
<regleMere xmlns:d4p1="http://schemas.datacontract.org/2004/07/V2DTO"
           xmlns:i="http://www.w3.org/2001/XMLSchema-instance"
           xmlns:z="http://schemas.microsoft.com/2003/10/Serialization/"
           z:Id="i${motherRule.MyGuid}">
    ${getXmlLine(motherRule, 'Id', {defaultValue: 0})}
    ${getXmlLine(motherRule, 'IdEnveloppe')}
    ${getXmlLine(motherRule, 'IdOperateur', {defaultValue: 0})}
    ${getXmlLine(motherRule, 'IdTypeArrondi')}
    ${getXmlLine(motherRule, 'IdTypeDate', {defaultValue: 0})}
    ${getXmlBoolean(motherRule, 'IsComplementaire')}
    ${getXmlBoolean(motherRule, 'IsForcageAutorise')}
    ${getXmlBoolean(motherRule, 'IsGroupe')}
    ${getXmlBoolean(motherRule, 'IsSubChoisie')}
    ${getXmlBoolean(motherRule, 'IsTransverse')}
    ${getXmlBoolean(motherRule, 'IsVenteSansSubAutorise')}
    ${getXmlLine(motherRule, 'Jours', {defaultValue: 0})}
    ${getXmlLine(motherRule, 'Libelle')}
    ${getXmlLine(motherRule, 'Mois', {defaultValue: 0})}
    ${
        arrayEmpty(motherRule.ReglesEnfants?.RegleAvanceeModelDTO)
            ? ''
            : wrapByXmlTag(motherRule.ReglesEnfants.RegleAvanceeModelDTO.map(
                rule => {
                    return advancedRuleToXML(rule, 'RegleAvanceeModelDTO', motherRule.MyGuid);
                }).join('\n'), 'ReglesEnfants')
    }
    ${getXmlLine(motherRule, 'Subvention', {defaultValue: 0})}
    ${getXmlLine(motherRule, 'SubventionDefaut', {defaultValue: 0})}
    ${getXmlLine(motherRule, 'TypeRegle')}
</regleMere>
    `;
}

export function advancedRuleFPAToXML(motherRule: RegleAvanceeModelDto): string {
    if (!motherRule) {
        return '';
    }

    if (!motherRule.Status) {
        motherRule.Status = motherRule.Id ? StatusDtoEnum.Updated : StatusDtoEnum.Inserted;
    }
    return `
<d4p1:RegleAvancee z:Id="i${motherRule.MyGuid}">
    ${getXmlLine(motherRule, 'Status')}
    ${getXmlLine(motherRule, 'Id', {defaultValue: 0})}
    ${getXmlLine(motherRule, 'IdEnveloppe')}
    ${getXmlLine(motherRule, 'IdOperateur', {defaultValue: 0})}
    ${getXmlLine(motherRule, 'IdTypeArrondi')}
    ${getXmlLine(motherRule, 'IdTypeDate', {defaultValue: 0})}
    ${getXmlBoolean(motherRule, 'IsComplementaire')}
    ${getXmlBoolean(motherRule, 'IsForcageAutorise')}
    ${getXmlBoolean(motherRule, 'IsGroupe')}
    ${getXmlBoolean(motherRule, 'IsSubChoisie')}
    ${getXmlBoolean(motherRule, 'IsTransverse')}
    ${getXmlBoolean(motherRule, 'IsVenteSansSubAutorise')}
    ${getXmlLine(motherRule, 'Jours', {defaultValue: 0})}
    ${getXmlLine(motherRule, 'Libelle')}
    ${getXmlLine(motherRule, 'Mois', {defaultValue: 0})}
    ${
        arrayEmpty(motherRule.ReglesEnfants?.RegleAvanceeModelDTO)
            ? ''
            : wrapByXmlTag(motherRule.ReglesEnfants.RegleAvanceeModelDTO.map(
                rule => {
                    return advancedRuleToXML(rule, 'RegleAvanceeModelDTO', motherRule.MyGuid);
                }).join('\n'), 'ReglesEnfants')
    }
    ${getXmlLine(motherRule, 'Subvention', {defaultValue: 0})}
    ${getXmlLine(motherRule, 'SubventionDefaut', {defaultValue: 0})}
    ${getXmlLine(motherRule, 'TypeRegle')}
</d4p1:RegleAvancee>
    `;
}

export function advancedRuleToXML(rule: RegleAvanceeModelDto, name: string, regleParentMyGuid: string): string {
    if (!rule) {
        return '';
    }

    const parentMyGuid = guid();

    if (!rule.Status) {
        rule.Status = rule.Id ? StatusDtoEnum.Updated : StatusDtoEnum.Inserted;
    }

    return `
<d4p1:${name} z:Id="i${parentMyGuid}">
    ${getXmlLine(rule, 'Status')}
    ${computationNodeToXML(rule.CalculSubvention, 'CalculSubvention')}
    ${computationNodeToXML(rule.CalculSubventionDefaut, 'CalculSubventionDefaut')}
    ${getXmlLine(rule, 'Id', {defaultValue: 0})}
    ${getXmlLine(rule, 'IdEnveloppe')}
    ${getXmlLine(rule, 'IdOperateur', {defaultValue: 0})}
    ${getXmlLine(rule, 'IdRegleApplication')}
    ${getXmlLine(rule, 'IdRegleNonApplication')}
    ${getXmlLine(rule, 'IdTypeArrondi')}
    ${getXmlLine(rule, 'IdTypeDate', {defaultValue: 0})}
    ${getXmlBoolean(rule, 'IsComplementaire', {defaultValue: false})}
    ${getXmlBoolean(rule, 'IsForcageAutorise', {defaultValue: false})}
    ${getXmlBoolean(rule, 'IsGroupe', {defaultValue: false})}
    ${getXmlBoolean(rule, 'IsSubChoisie', {defaultValue: false})}
    ${getXmlBoolean(rule, 'IsTransverse', {defaultValue: false})}
    ${getXmlBoolean(rule, 'IsVenteSansSubAutorise', {defaultValue: false})}
    ${getXmlLine(rule, 'Jours', {defaultValue: 0})}
    ${getXmlLine(rule, 'Libelle', {defaultValue: 0})}
    ${getXmlLine(rule, 'Mois', {defaultValue: 0})}
    ${moleculeToXML(rule.Molecule, 'Molecule')}
    <d4p1:RegleParent z:Ref="i${regleParentMyGuid}"></d4p1:RegleParent>
    ${arrayEmpty(rule.ReglesEnfants?.RegleAvanceeModelDTO)
        ? '<d4p1:ReglesEnfants></d4p1:ReglesEnfants>'
        : wrapByXmlTag(rule.ReglesEnfants?.RegleAvanceeModelDTO.map(
            child => {
                return advancedRuleToXML(child, 'RegleAvanceeModelDTO', parentMyGuid);
            }).join('\n'), 'ReglesEnfants')
    }
    ${getXmlLine(rule, 'Subvention', {defaultValue: 0})}
    ${getXmlLine(rule, 'SubventionDefaut', {defaultValue: 0})}
    ${getXmlLine(rule, 'TypeRegle')}
</d4p1:${name}>
    `;
}

export function computationNodeToXML(node: CalculRegleAvanceeModelDto, name = 'CalculRegleAvanceeModelDTO'): string {
    if (!node) {
        return '';
    }

    if (!node.MyGuid) {
        node.MyGuid = guid();
    }

    return `
<d4p1:${name} z:Id="i${node?.MyGuid}">
    ${node?.CalculParent?.Status === StatusDtoEnum.Referenced
        ? `<d4p1:CalculParent z:Ref="i${node.CalculParent.MyGuid}"></d4p1:CalculParent>`
        : computationNodeToXML(node?.CalculParent, 'CalculParent')
    }

    ${arrayEmpty(node?.CalculsEnfants?.CalculRegleAvanceeModelDTO)
        ? '<d4p1:CalculsEnfants></d4p1:CalculsEnfants>'
        : wrapByXmlTag(node?.CalculsEnfants?.CalculRegleAvanceeModelDTO.map(c => computationNodeToXML(c)).join(''), 'CalculsEnfants')
    }

    ${criterionToXML(node?.Critere)}
    ${getXmlLine(node, 'Id', {defaultValue: 0})}
    ${getXmlLine(node, 'IdOperateur', {defaultValue: 0})}
    ${getXmlBoolean(node, 'IsGroupe', {defaultValue: false})}
    ${getXmlLine(node, 'Ordre')}
    ${getXmlLine(node, 'Valeur')}
</d4p1:${name}>
    `;
}

export function criterionToXML(criterion: CritereRegleAvanceeModelDto): string {
    if (!criterion) {
        criterion = {
            Id: 0,
        };
    }
    if (!criterion.MyGuid) {
        criterion.MyGuid = guid();
    }

    return `
<d4p1:Critere z:Id="i${criterion?.MyGuid}">
    ${getXmlLine(criterion, 'Id')}
    ${arrayEmpty(criterion?.Parametres?.MoleculeRegleAvanceeModelDTO)
        ? '<d4p1:Parametres></d4p1:Parametres>'
        : wrapByXmlTag(criterion.Parametres.MoleculeRegleAvanceeModelDTO.map(p => moleculeToXML(p)).join(''), 'Parametres')
    }
</d4p1:Critere>
    `;
}

export function moleculeToXML(molecule: MoleculeRegleAvanceeModelDto, name = 'MoleculeRegleAvanceeModelDTO'): string {
    if (!molecule) {
        return '';
    }
    const myUid = molecule.MyGuid || guid();
    return `
<d4p1:${name} z:Id="i${myUid}">
    ${computationNodeToXML(molecule.CalculValeur, 'CalculValeur')}
    ${criterionToXML(molecule.Critere)}
    ${getXmlLine(molecule, 'IdChamp')}
    ${getXmlLine(molecule, 'IdOperateur')}
    ${getXmlLine(molecule, 'TypeValeur')}
    ${getXmlLine(molecule, 'Valeur')}
</d4p1:${name}>
    `;
}
